import React, { useState } from "react";
import { Image } from "react-bootstrap";
import { getInitials, initialsPaletteColor } from "../utils/avatars";

/**
 * AvatarWithLabel - Profile picture with colored border and type label
 *
 * Displays a circular profile image with an SVG overlay that shows:
 * - A colored border ring matching the profile type
 * - A text label curved along the bottom of the circle
 *
 * Styling comes from CSS classes in profile-types.scss, making CSS the
 * single source of truth for profile type theming.
 *
 * @param {string} profileType - One of: "media", "promoter_pr", "promoter_expert", "promoter_hybrid"
 * @param {number} size - Diameter in pixels (default: 100)
 * @param {string} className - Additional CSS classes for the image
 * @param {string} src - Image source URL
 * @param {string} alt - Image alt text
 */
function AvatarWithLabel({ profileType, size = 100, className = "", fallbackToInitials = false, ...rest }) {
  const { src, alt, onError: userOnError, ...restWithoutImageProps } = rest;

  // Opt-in recovery, mirroring ImageCropped: an off-site avatar (e.g. the DiceBear default) that
  // fails to load swaps to a locally-drawn initials circle instead of leaving a broken-image icon
  // inside the ring/label SVG. Stores the failed src itself (not a boolean) so the failed state
  // derives synchronously during render when src changes -- no reset effect, no one-frame flash.
  const [failedSrc, setFailedSrc] = useState(null);
  const hasFailed = Boolean(src) && failedSrc === src;

  const missingSrc = !src;
  const handleError = (event) => {
    if (fallbackToInitials) setFailedSrc(src);
    if (userOnError) userOnError(event);
  };
  const attachError = fallbackToInitials || Boolean(userOnError);

  // Configuration per profile type (only non-CSS properties)
  const config = {
    media: { label: "media", textStartOffset: "22%" },
    promoter_pr: { label: "PR", textStartOffset: "36%" },
    promoter_expert: { label: "expert", textStartOffset: "20%" },
    promoter_hybrid: { label: "expert / pr", textStartOffset: "4%", smallText: true },
  };

  const { label = "", textStartOffset = "25%", smallText = false } = config[profileType] || {};

  // Convert profileType to CSS class format (promoter_expert -> promoter-expert)
  const profileTypeClass = profileType ? profileType.replace("_", "-") : "";
  const textClass = smallText ? "profile-type-text-sm" : "profile-type-text";

  const width = `${size}px`;
  const height = `${size}px`;

  const renderInitials = () => (
    <div
      className={`d-inline-flex align-items-center justify-content-center rounded-circle text-white fw-bold ${className}`}
      style={{
        width: "100%",
        height: "100%",
        backgroundColor: initialsPaletteColor(alt),
        fontFamily: "sans-serif, Helvetica",
        fontSize: `${size * 0.4}px`,
        lineHeight: 1,
      }}
      aria-label={alt}
      data-testid="avatar-with-label-initials"
    >
      {getInitials(alt)}
    </div>
  );

  const renderImage = () => (
    <Image
      roundedCircle
      className={`object-cover ${className}`}
      style={{ width: "100%", height: "100%" }}
      src={src}
      alt={alt}
      onError={attachError ? handleError : undefined}
      {...restWithoutImageProps}
    />
  );

  return (
    <div className={`profile-type ${profileTypeClass} d-inline-block`} style={{ position: "relative", width, height }}>
      {fallbackToInitials && (hasFailed || missingSrc) ? renderInitials() : renderImage()}
      <svg style={{ position: "absolute", top: 0, left: 0 }} viewBox="0 0 100 100" xmlns="http://www.w3.org/2000/svg">
        <defs>
          <path id="textPath" d="M 3 50 A 46 47 0 0 0 50 97" />
        </defs>
        <circle cx="50" cy="50" r="48.4" className="profile-type-stroke" strokeWidth="3" fill="transparent" />
        {label && (
          <>
            <path
              d="M 50 94.9 A 50 50 0 0 1 5.7 50"
              className="profile-type-stroke"
              strokeWidth="10"
              fill="transparent"
            />
            <text className={textClass}>
              <textPath href="#textPath" startOffset={textStartOffset}>
                {label}
              </textPath>
            </text>
          </>
        )}
      </svg>
    </div>
  );
}

export default AvatarWithLabel;
